// components/modals/skillsShared.jsx
//
// Pieces shared by the Skills tab (SkillsContent.jsx) and the skill editor
// (SkillEditor.jsx).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiSearch, FiTool, FiX } from 'react-icons/fi';

export function slugify(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// fetchWithHandling (services/api.js) throws the backend's raw error
// message as a plain string on a non-OK response (not an Error), so
// `err.message` is always undefined for those -- silently swallowing
// specific messages like the 409 "slug already exists" or the 400
// "unknown tool name" response and falling back to a generic string.
// A genuine JS/network failure (e.g. fetch() itself throwing) still comes
// through as a real Error, hence the typeof check rather than assuming one
// shape or the other.
export function errorMessage(err, fallback) {
  if (typeof err === 'string' && err) return err;
  return err?.message || fallback;
}

export function ToolPicker({ id, availableTools, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleTool = (toolName) => {
    if (selected.includes(toolName)) {
      onChange(selected.filter((t) => t !== toolName));
    } else {
      onChange([...selected, toolName]);
    }
  };

  const filteredTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableTools;
    return availableTools.filter(
      (tool) =>
        tool.name.toLowerCase().includes(q) ||
        tool.description?.toLowerCase().includes(q),
    );
  }, [availableTools, query]);

  return (
    <div ref={containerRef} className="relative">
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((toolName) => (
            <span
              key={toolName}
              className="flex items-center gap-1 rounded-full border border-[#5652CF]/40 bg-[#5652CF]/10 py-0.5 pl-2.5 pr-1.5 text-[11px] font-medium text-[#c7c5f7]"
            >
              <FiTool className="h-2.5 w-2.5 flex-shrink-0" />
              {toolName}
              <button
                type="button"
                onClick={() => toggleTool(toolName)}
                className="rounded-full p-0.5 text-[#c7c5f7]/70 hover:bg-[#5652CF]/30 hover:text-white"
                aria-label={`Remove ${toolName}`}
              >
                <FiX className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <button
        id={id}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-md border border-zinc-700 bg-zinc-900 p-2 text-left text-sm text-zinc-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
      >
        <span className="truncate text-zinc-400">
          {selected.length > 0
            ? 'Add another tool...'
            : 'Select tools this skill may use'}
        </span>
        <FiChevronDown
          className={`flex-shrink-0 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-zinc-700 bg-zinc-900 shadow-lg">
          <div className="relative border-b border-zinc-700 p-1.5">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-3 w-3 -translate-y-1/2 text-zinc-500" />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools..."
              className="w-full rounded bg-zinc-800 py-1.5 pl-7 pr-2 text-xs text-zinc-200 outline-none placeholder:text-zinc-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {filteredTools.length === 0 ? (
              <p className="p-2 text-xs text-zinc-500">
                {availableTools.length === 0
                  ? 'No tools available.'
                  : 'No tools match your search.'}
              </p>
            ) : (
              filteredTools.map((tool) => (
                <label
                  key={tool.name}
                  className="flex cursor-pointer items-start gap-2 rounded p-2 text-xs hover:bg-zinc-800"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(tool.name)}
                    onChange={() => toggleTool(tool.name)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium text-zinc-200">
                      {tool.name}
                    </span>
                    {tool.description && (
                      <span className="block text-zinc-500">
                        {tool.description}
                      </span>
                    )}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
